import { z } from 'zod';
import { AppError } from './errors/base.ts';

/**
 * Every runtime env var, validated once at boot. Add a var here (+ to
 * `.env.example`) rather than reaching for `process.env.X!` anywhere else —
 * that scatters unvalidated access and defeats fail-fast startup.
 */
/** RFC 7230 `token` — the only characters a valid HTTP header name may contain. */
const HTTP_HEADER_NAME = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;

export const envSchema = z
  .object({
    NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
    PORT: z.coerce.number().int().positive().default(3000),
    DATABASE_URL: z.string().url(),
    DRAGONFLY_URL: z.string().url(),
    ROUTER_URL: z.string().url(),
    // The shared bearer every Router (`apps/router`) caller presents on `/v1`.
    // The SAME value the Router itself boots with — it is one credential, checked
    // on one side and sent on the other, so a mismatch is a total chat outage and
    // a hard one to read from either log alone.
    // Optional here but REQUIRED IN PRODUCTION (see the `superRefine` below), and
    // required by the Router itself, which refuses to boot without it. Unset
    // off-production ⇒ the Router serves `/v1` open and this side sends no header,
    // so `./bin/dev` and the offline suites stay zero-configuration.
    //
    // `.trim()` FIRST, because every consumer trims (`RouterClient`,
    // `RouterModelProvider`, and `RouterAuth::resolve` on the Rust side) and a
    // check on the untrimmed value guarantees nothing about what is actually
    // sent: a value of 16 spaces passed a bare `.min(16)`, then trimmed to
    // nothing at the client, so the pod booted green and sent NO `Authorization`
    // header — precisely the silent total outage the production check below
    // exists to prevent. Trimming also normalizes the `KEY=<value>\n` a k8s
    // Secret mount produces into the exact bytes the Router compares against.
    //
    // Printable ASCII, no spaces (RFC 7230 `token68` territory) for two reasons.
    // A non-ASCII value is UNSENDABLE: `Headers` throws on the assembled
    // `Bearer …`, so both sides boot clean, `/healthz` stays green, and every
    // single LLM call dies inside `fetch`. And it is what makes this floor
    // genuinely the same floor as the Router's `MIN_KEY_LEN`: JS counts UTF-16
    // code units where Rust counts scalars (`'😀'.repeat(8)` is 16 here and 8
    // there), and JS `.trim()` strips U+FEFF where Rust's does not — restricted
    // to ASCII the two agree byte for byte.
    //
    // A BLANK value is `undefined`, not a violation — the same reading
    // `RouterAuth::resolve` takes (`.filter(|key| !key.is_empty())` after its
    // own trim). A shell that exports the variable unconditionally (`./bin/dev`
    // did, so every `bun` child saw `ROUTER_API_KEY=""`) or a ConfigMap key
    // written with no value means "not configured", and off-production that is
    // a supported, zero-configuration state — reading it as a malformed key
    // instead crash-looped the edge API and the jobs worker at boot. Production
    // is unaffected: blank resolves to absent, and absent fails the
    // required-in-production check below.
    ROUTER_API_KEY: z.preprocess(
      (raw) => (typeof raw === 'string' && raw.trim() === '' ? undefined : raw),
      z
        .string()
        .trim()
        .regex(/^[\x21-\x7e]+$/, 'ROUTER_API_KEY must be printable ASCII with no spaces')
        .min(16)
        .optional(),
    ),
    JWT_SECRET: z.string().min(16),
    // Auth — all optional: unset is the safe local default. Without HCAPTCHA_SECRET
    // the password rails skip the bot-gate (NullCaptchaVerifier); without the
    // Google pair, `/auth/google` reports unavailable. Set all three in production.
    HCAPTCHA_SECRET: z.string().min(1).optional(),
    GOOGLE_CLIENT_ID: z.string().min(1).optional(),
    GOOGLE_CLIENT_SECRET: z.string().min(1).optional(),
    // Age-assurance vendor (Yoti / Persona-class ID + age-estimation) behind the
    // `AgeVerifier` seam. Both optional. In NON-production the offline
    // `StubAgeVerifier` runs the whole signup + geo-gate loop with no vendor account
    // (a hard-block region's `vendor` method is exercised via a stubbed
    // vendorReference). In PRODUCTION the composition root NEVER ships that stub:
    // with BOTH set it wires the real `VendorAgeVerifier`; without them it FAILS
    // CLOSED (`FailClosedAgeVerifier`), so a hard-block UK OSA / US post-Paxton
    // region cannot clear `age_ok` on an unchecked vendorReference. We persist only
    // the vendor's pass/fail + timestamp — never the ID document (minimal PII,
    // compliance.md).
    AGE_VERIFY_API_KEY: z.string().min(1).optional(),
    AGE_VERIFY_BASE_URL: z.string().url().optional(),
    // Networks the edge's OWN reverse proxies live on (comma-separated CIDRs).
    // This is the trust boundary for every header a caller could otherwise forge:
    // `x-forwarded-for`, `x-real-ip`, and above all the IP-country geo headers the
    // 18+ geo-gate reads. A request whose socket peer is NOT in this set is treated
    // as a direct connection from the internet — its forwarding/geo headers are
    // discarded and its region resolves from the peer address alone. Unset ⇒
    // loopback + RFC1918 + link-local (`PRIVATE_NETWORK_CIDRS`), which is correct
    // for the local front door and an in-cluster ingress; TIGHTEN it to the
    // ingress's own range in a deployment where other pods can reach the api
    // directly. Widening it to `0.0.0.0/0` would restore the caller-controlled-geo
    // bug this exists to close.
    TRUSTED_PROXY_CIDRS: z.string().min(1).optional(),
    // The ONE IP-country header this deployment's front door writes, and the
    // subdivision header it writes ALONGSIDE it. Only these exact names are read;
    // every other spelling is stripped, never trusted. Deliberately not a menu: a
    // reverse proxy does not delete headers it did not write, so accepting several
    // spellings lets a caller behind the proxy shadow the real one — and accepting
    // a subdivision from a different header family than the country hands them the
    // exact field the US post-Paxton rule turns on (`x-geo-country: US` from the
    // edge + a self-supplied `x-geo-subdivision: CA` ⇒ `self_attest`).
    // REQUIRED IN PRODUCTION (see the `superRefine` below): with no declared
    // header no caller's region can ever resolve, the geo policy fails closed to
    // `vendor` for everyone, and every explicit surface 403s — an outage that
    // would otherwise look healthy. Non-production defaults to `x-geo-country` /
    // `x-geo-subdivision`.
    // ⚠ Only set TRUSTED_GEO_SUBDIVISION_HEADER if the edge actually resolves
    // states; declaring one it never writes is the bypass described above.
    // ⚠ The front door must OVERWRITE both on every request (nginx
    // `proxy_set_header`, not `add_header`), including when it resolves nothing.
    // RFC 7230 `token` charset: a name with a space/CRLF/non-ASCII byte makes
    // `Headers.get` throw at REQUEST time, which from a `use('*')` middleware is a
    // 500 on every route including /health. Reject it at boot instead.
    TRUSTED_GEO_COUNTRY_HEADER: z.string().min(1).max(64).regex(HTTP_HEADER_NAME).optional(),
    TRUSTED_GEO_SUBDIVISION_HEADER: z.string().min(1).max(64).regex(HTTP_HEADER_NAME).optional(),
    // NCMEC reporting (the red-line breach actuator) behind the `ReportSink` seam.
    // Both optional. In NON-production the composition root wires the fake
    // `NullReportSink` — dev/test never bans or files. In PRODUCTION it wires the
    // real `NcmecReportSink`, which ALWAYS hard-bans a red-line offender (a local
    // `accounts.disabled_at` write — no creds needed) and, WHEN both are set, files
    // the CyberTipline report + preserves evidence over the shared HTTP transport. A
    // due report with no endpoint/key configured is logged at error level (surfaced
    // to GlitchTip), never silently dropped. We send only the incident category +
    // opaque subject ids + a preserve flag — never the offending bytes (minimal PII,
    // compliance.md § "Record-keeping").
    NCMEC_REPORT_URL: z.string().url().optional(),
    NCMEC_API_KEY: z.string().min(1).optional(),
    // Output age/identity classifier that scans the generated PIXELS (image/video)
    // behind the `ContentClassifier` seam — the "age classifier on every generated
    // image/video" (compliance.md). Both optional. In NON-production the composition
    // root wires the offline `HeuristicMediaModerator` — it ABSTAINS on pixels, so
    // every produced asset is held for `review` (fail closed, never auto-served). In
    // PRODUCTION, with BOTH set it wires the real `HttpPixelClassifier`
    // (`ClassifierMediaModerator`); either unset ⇒ the offline moderator still holds
    // every asset for review. A vendor OUTAGE fails closed (abstain → review), never
    // an auto-serve. We send only the produced bytes + content-type, never the
    // account/user identity (minimal PII, compliance.md).
    PIXEL_CLASSIFIER_URL: z.string().url().optional(),
    PIXEL_CLASSIFIER_API_KEY: z.string().min(1).optional(),
    // INPUT text classifier — the pre-generation gate on the two PROMPT gateways
    // (a chat prompt and a media generation prompt) behind the same
    // `ContentClassifier` seam. NOT the builder's persona/backstory screen: that
    // still runs the deterministic `HeuristicPersonaScreener` only (`subject='persona'`
    // never reaches a `ContentClassifier`) — a separate, still-open gap.
    //
    // DISTINCT creds from `PIXEL_CLASSIFIER_*`: that vendor scans produced BYTES
    // and would be handed text it cannot judge (the pixel adapter silently falls
    // back to the offline scan, so pointing the text gate at it is a fail-OPEN
    // misconfiguration).
    //
    // Optional here but REQUIRED IN PRODUCTION (see the `superRefine` below), and
    // that asymmetry with `PIXEL_CLASSIFIER_*` is deliberate: an unconfigured PIXEL
    // vendor degrades (abstain → `review`, the product works and the queue fills),
    // whereas an unconfigured TEXT vendor is a total outage — `evaluateInput` maps
    // its abstain to `block`, so EVERY chat turn, generation and companion build
    // 422s. Those are ordinary business-outcome statuses, so nothing pages: the
    // deploy looks healthy and answers nothing. Failing at BOOT makes the
    // misconfiguration loud (a CrashLoop with this message) instead of silent.
    //
    // In NON-production the composition roots wire the offline `OfflineClassifier`
    // — the deterministic red-line scan, so local dev + tests need no cloud. With
    // both set they wire the real `HttpTextClassifier` (model-scored, layered on
    // top of the always-on red lines). The `AbstainingClassifier` branch remains as
    // defense in depth for the runtime case the boot check cannot cover: a vendor
    // OUTAGE mid-flight also abstains, and an abstain never allows.
    //
    // We send only the subject text — never the account/user identity. The subject
    // text IS user content (a chat message), so the vendor is a subprocessor
    // (compliance.md § "Classifier providers behind an interface").
    TEXT_CLASSIFIER_URL: z.string().url().optional(),
    TEXT_CLASSIFIER_API_KEY: z.string().min(1).optional(),
    // Upload face/consent moderator that screens an uploaded CONCEPT image on the
    // builder's `build_source='upload'` path (red line 2 — NCII/real-person; the
    // "upload path is your own concept only" check, compliance.md) behind the
    // `UploadModerator` seam. Both optional. In NON-production the composition root
    // wires the offline `OfflineUploadModerator` — it HOLDS every uploaded concept
    // for the admin `review` queue (fail closed, never auto-attached). In PRODUCTION,
    // with BOTH set AND the phase-5 e2 presign resolver wired, it wires the real
    // `HttpUploadModerator`: a real, identifiable person → `block` (rejected), an
    // affirmatively-clean own concept → `allow`. A vendor OUTAGE (or an unresolvable
    // asset) FAILS CLOSED (→ review), never an auto-attach. We send only a short-TTL
    // presigned image URL — never the account/user identity (minimal PII).
    UPLOAD_MODERATOR_URL: z.string().url().optional(),
    UPLOAD_MODERATOR_API_KEY: z.string().min(1).optional(),
    // Kubeez gateway credential — ONE Bearer key + host shared by media generation
    // (image/edit/video via `router-generate` → `router-task/<id>`, behind the
    // `MediaProvider` seam) AND the Rust Router's uncensored chat adapter
    // (`kubeez-character` via `router-chat`, which reads this same env). Optional:
    // unset ⇒ the composition root registers ONLY the offline `MockMediaProvider`
    // (the enqueue → download → e2 loop runs locally with no cloud) and the Router's
    // Kubeez chat fast-fails to its mock. Set it in production to run both live.
    // `KUBEEZ_ROUTER_URL` has a safe default baked in (the gateway base); override
    // only to point at a sandbox host.
    KUBEEZ_ROUTER_KEY: z.string().min(1).optional(),
    KUBEEZ_ROUTER_URL: z.string().url().optional(),
    // The system account owning the PREBUILT catalog templates the free front door
    // lists (`scripts/companions/seed-prebuilt.ts` seeds them under a deterministic
    // id). Optional: the edge falls back to that seeded id, so a standard deploy
    // needs no override. Set it only if the catalog lives under a different account.
    CATALOG_ACCOUNT_ID: z.string().min(1).optional(),
    // Object storage (iDrive e2 / S3-compatible). All optional in dev/test; required in production.
    E2_ENDPOINT: z.string().url().optional(),
    E2_ACCESS_KEY_ID: z.string().min(1).optional(),
    E2_SECRET_ACCESS_KEY: z.string().min(1).optional(),
    E2_BUCKET_PUBLIC: z.string().min(1).optional(),
    E2_BUCKET_PRIVATE: z.string().min(1).optional(),
    E2_FORCE_PATH_STYLE: z
      .enum(['true', 'false'])
      .transform((v) => v === 'true')
      .optional(),
    // Web Push (VAPID) for proactive messaging + unlock comments. All optional:
    // unset means the offline `NoopWebPushTransport` — the proactive message still
    // persists and relays over WS, but zero device notifications are sent. Set all
    // three in production (generate locally with `bunx web-push generate-vapid-keys`).
    // `VAPID_SUBJECT` is the contact URL sent to push services — a `mailto:` or
    // `https:` URL identifying this app (proactive-messaging.md § "Local dev").
    VAPID_PUBLIC_KEY: z.string().min(1).optional(),
    VAPID_PRIVATE_KEY: z.string().min(1).optional(),
    VAPID_SUBJECT: z.string().min(1).optional(),
    // Jina AI — the `web_search` tool's search + reader backend. Optional: unset
    // means the offline canned client (`OfflineJinaClient`), so `web_search` and its
    // per-user daily quota exercise fully offline. Set it in production for live
    // `s.jina.ai` search + `r.jina.ai` reads (Bearer, server-side only).
    JINA_API_KEY: z.string().min(1).optional(),
    // CCBill (the payment rail). All optional, because the rail is not deployed —
    // but "optional" NEVER means "unverified". With an empty/unset `CCBILL_SALT`
    // the `CCBillRail` REJECTS every inbound webhook (fail closed) and, in
    // production, the edge does not even mount `/webhooks/ccbill`. Local dev sets a
    // throwaway salt (`.env.development`) that the simulator co-signs with, so the
    // whole checkout → webhook → credits loop still runs with no live account
    // (billing.md § "Local dev"). Checkout URL building needs no secret.
    // `CCBILL_ACCOUNT`/`CCBILL_SUBACCOUNT` are the FlexForms client account +
    // sub-account stamped on the checkout URL.
    CCBILL_ACCOUNT: z.string().optional(),
    CCBILL_SUBACCOUNT: z.string().optional(),
    CCBILL_SALT: z.string().optional(),
    // The DISCREET card-statement descriptor CCBill (Merchant-of-Record) shows on
    // the buyer's statement — mirrored here so the descriptor is verified in code,
    // never adult-identifying (compliance.md § "Billing discretion"). Optional:
    // unset ⇒ the discreet `DISCREET_STATEMENT_DESCRIPTOR` default. The `CCBillRail`
    // REFUSES to construct with an indiscreet value, so a misconfiguration fails
    // fast at boot instead of leaking on a customer's statement.
    CCBILL_DESCRIPTOR: z.string().min(1).optional(),
    // CCBill DataLink — the server-side subscription-management API the production
    // `CCBillDatalinkCanceller` uses to stop rebilling. Credentials are DISTINCT
    // from the FlexForms account above. All optional: unset ⇒ the offline
    // `NoopSubscriptionCanceller` fake (dev/test/sandbox — the `Cancellation`
    // webhook stays the state of record). Set BOTH user + password in production to
    // wire the live canceller. `CCBILL_DATALINK_URL` overrides the endpoint (e.g. a
    // sandbox host); unset ⇒ the DataLink default.
    CCBILL_DATALINK_USERNAME: z.string().min(1).optional(),
    CCBILL_DATALINK_PASSWORD: z.string().min(1).optional(),
    CCBILL_DATALINK_URL: z.string().url().optional(),
    // Error tracking (GlitchTip, Sentry-API-compatible) — one DSN PER APP. Optional:
    // unset ⇒ errors are not reported off-box (safe local default). Set per app in
    // production so runtime failures surface in the shared GlitchTip project
    // (CLAUDE.md § "Observability & MCP"). NOT required in production: a missing
    // DSN is a blind spot, not an outage, and crash-looping every pod over the
    // error tracker would be a self-inflicted one.
    //
    // A BLANK value is `undefined`, not a violation — the same reading
    // `ROUTER_API_KEY` above takes, and the one `resolveDsn`
    // (`observability.ts`) applies at the consuming end. `.env.example` ships
    // `GLITCHTIP_DSN=` with no value, a ConfigMap key can be written empty, and a
    // shell that exports the variable unconditionally produces `''` — under a bare
    // `.url()` every one of those is a hard `EnvValidationError` at boot, so the
    // documented "unset ⇒ no reporting" default would instead crash-loop the api
    // pod, the jobs worker and the marketing server on a variable none of them
    // needs.
    GLITCHTIP_DSN: z.preprocess(
      (raw) => (typeof raw === 'string' && raw.trim() === '' ? undefined : raw),
      z.string().url().optional(),
    ),
  })
  .superRefine((data, ctx) => {
    // Prod env enforcement: fail-fast at boot if any required secret is missing.
    if (data.NODE_ENV === 'production') {
      // NOTE — the CCBill vars are deliberately NOT required in production. The
      // CCBill rail was DROPPED from the deploy (see the infrastructure repo's
      // `stacks/apps/4fantasia/manifests/kustomization.yml` + README), so a
      // required-in-prod check here CrashLooped the api pod at boot the moment the
      // Deployment was enabled — a hard outage for every non-billing surface
      // (chat, companions, media) over a rail that no longer exists.
      //
      // Absence degrades gracefully instead, and every degradation is explicit at
      // the composition root (`apps/api/src/composition/build-billing.ts`) — and
      // NONE of them weakens verification:
      //   - no `CCBILL_SALT`  ⇒ in production the edge does NOT mount
      //     `/webhooks/ccbill` at all (`ccbillWebhooksMountable`), and the rail
      //     rejects any webhook it cannot verify wherever it IS mounted. Set the
      //     salt before re-enabling the rail. (It used to SKIP verification here,
      //     which would have applied any stranger's POST as a real payment.)
      //   - no `CCBILL_ACCOUNT`/`CCBILL_SUBACCOUNT` ⇒ checkout URLs are built with
      //     empty ids, so a checkout attempt fails visibly at the rail rather than
      //     silently charging the wrong merchant.
      //   - no `CCBILL_DATALINK_*` ⇒ the offline `NoopSubscriptionCanceller`.
      // Re-introducing a payment rail = re-introducing its own required-in-prod
      // block here, next to that rail's composition (OCP), not reviving this one.

      // Object storage (iDrive e2) — all required in production.
      if (!data.E2_ENDPOINT) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['E2_ENDPOINT'],
          message: 'E2_ENDPOINT is required in production',
        });
      }
      if (!data.E2_ACCESS_KEY_ID) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['E2_ACCESS_KEY_ID'],
          message: 'E2_ACCESS_KEY_ID is required in production',
        });
      }
      if (!data.E2_SECRET_ACCESS_KEY) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['E2_SECRET_ACCESS_KEY'],
          message: 'E2_SECRET_ACCESS_KEY is required in production',
        });
      }
      if (!data.E2_BUCKET_PUBLIC) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['E2_BUCKET_PUBLIC'],
          message: 'E2_BUCKET_PUBLIC is required in production',
        });
      }
      if (!data.E2_BUCKET_PRIVATE) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['E2_BUCKET_PRIVATE'],
          message: 'E2_BUCKET_PRIVATE is required in production',
        });
      }
      // The Router's shared bearer — required in production on BOTH sides. The
      // Router refuses to boot without it, so an api/jobs pod that lacks it would
      // send no `Authorization` header and get `401` on every LLM call: chat,
      // memory extraction, and proactive turns all dead, while `/healthz` stays
      // green. Fail at BOOT here too, so the misconfiguration surfaces as a
      // crash-loop on the pod that is actually missing the secret.
      if (!data.ROUTER_API_KEY) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['ROUTER_API_KEY'],
          message:
            'ROUTER_API_KEY is required in production — the Router rejects every ' +
            'unauthenticated /v1 call, so without it no LLM request can be served',
        });
      }
      // Bot verification gate — required in production.
      if (!data.HCAPTCHA_SECRET) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['HCAPTCHA_SECRET'],
          message: 'HCAPTCHA_SECRET is required in production',
        });
      }
      // The INPUT text classifier — required in production, unlike every other
      // vendor pair here, because its absence is not a degradation: the gate
      // abstains, `evaluateInput` maps an abstain to `block`, and every chat turn,
      // every generation and every companion build answers `422`. Those are normal
      // business statuses, so no alert fires and `/healthz` stays green — a pod
      // that serves nothing while looking healthy. Fail at BOOT instead.
      if (!data.TEXT_CLASSIFIER_URL) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['TEXT_CLASSIFIER_URL'],
          message:
            'TEXT_CLASSIFIER_URL is required in production — without it the input gate ' +
            'abstains and blocks every prompt',
        });
      }
      if (!data.TEXT_CLASSIFIER_API_KEY) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['TEXT_CLASSIFIER_API_KEY'],
          message:
            'TEXT_CLASSIFIER_API_KEY is required in production — without it the input gate ' +
            'abstains and blocks every prompt',
        });
      }
      // The 18+ geo gate now fails CLOSED on an unresolved region (an absent geo
      // signal is not evidence of a lax jurisdiction). With no declared front-door
      // header, no caller's region resolves, every account is held at `vendor`
      // assurance, and every explicit surface answers `403` — while `/healthz`
      // stays green, exactly the silent-outage shape as the classifier above.
      // Same reasoning, same remedy: fail at BOOT.
      if (!data.TRUSTED_GEO_COUNTRY_HEADER) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['TRUSTED_GEO_COUNTRY_HEADER'],
          message:
            'TRUSTED_GEO_COUNTRY_HEADER is required in production — without it no caller’s ' +
            'region resolves and the 18+ geo gate holds everyone at vendor assurance',
        });
      }
      // A subdivision header without a country one is meaningless (the region is
      // built country-first) and reads as a configured geo source that is not one.
      if (data.TRUSTED_GEO_SUBDIVISION_HEADER && !data.TRUSTED_GEO_COUNTRY_HEADER) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['TRUSTED_GEO_SUBDIVISION_HEADER'],
          message: 'TRUSTED_GEO_SUBDIVISION_HEADER needs TRUSTED_GEO_COUNTRY_HEADER',
        });
      }
    }
  });

export type Env = z.infer<typeof envSchema>;

/** Thrown when the environment fails validation — boot must abort, never limp on. */
export class EnvValidationError extends AppError {
  readonly code = 'invalid_env';
  constructor(issues: z.ZodError) {
    const summary = issues.issues
      .map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`)
      .join('; ');
    super(`Invalid environment: ${summary}`, { statusCode: 500 });
  }
}

/**
 * Parse + validate the environment, failing fast with a readable error. Inject
 * the result into the composition root rather than reading `process.env` deep
 * in a service.
 */
export function loadEnv(source: Record<string, string | undefined> = process.env): Env {
  const parsed = envSchema.safeParse(source);
  if (!parsed.success) {
    throw new EnvValidationError(parsed.error);
  }
  return parsed.data;
}
